import { useState, Fragment } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../ui/table";
import {
  FolderKanban, Plus, Upload, Edit, Trash2, FileText, FileDown, Award, Loader2, Leaf, AlertTriangle, CalendarRange, ChevronDown, ChevronUp,
} from "lucide-react";
import { formatFullCurrency, getStatusBadge, getBalanceIndicator, hasIncomeMismatch } from "./projectUtils";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "../ui/tooltip";
import { AnnualBreakdownTable } from "./AnnualBreakdownTable";
import { InnoHubRefIcon } from "../shared/InnoHubRefLink";

export function ProjectsTable({
  filteredBalances, searchTerm, statusFilter,
  onNewProject, onImportClick,
  onEdit, onDelete, onExportFicha, onOpenCierre, fichaLoading, token,
}) {
  const [expandedId, setExpandedId] = useState(null);
  if (filteredBalances.length === 0) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <FolderKanban className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-lg font-medium mb-2">No hay proyectos</h3>
          <p className="text-muted-foreground mb-4">
            {searchTerm || statusFilter !== "all"
              ? "No se encontraron proyectos con esos filtros"
              : "Crea tu primer proyecto o importa datos desde Excel"}
          </p>
          <div className="flex gap-2 justify-center">
            <Button onClick={onNewProject} data-testid="new-project-empty-btn">
              <Plus className="w-4 h-4 mr-2" />Nuevo Proyecto
            </Button>
            <Button variant="outline" onClick={onImportClick}>
              <Upload className="w-4 h-4 mr-2" />Importar Excel
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <TooltipProvider>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[200px]">Proyecto</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Período</TableHead>
                <TableHead>Responsable</TableHead>
                <TableHead className="text-right">Presupuesto</TableHead>
                <TableHead className="text-right">Gasto Real</TableHead>
                <TableHead className="text-right">Ingresos</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead className="text-right">Subvención</TableHead>
                <TableHead className="text-right">Ahorro Fin.</TableHead>
                <TableHead className="text-right">Deducción Fiscal</TableHead>
                <TableHead className="w-[100px]">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredBalances.map((project) => {
                const mismatch = hasIncomeMismatch(project);
                return (
                <Fragment key={project.id}>
                <TableRow
                  data-testid={`project-row-${project.id}`}
                  className={project.balance < 0 ? "bg-red-50 dark:bg-red-950/20" : ""}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <FolderKanban className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <p className="font-medium">{project.project_name}</p>
                          {project.project_code && (
                            <span
                              className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                              data-testid={`project-code-${project.id}`}
                            >
                              {project.project_code}
                            </span>
                          )}
                          <InnoHubRefIcon refs={project.refs} />
                          {project.is_derived_view && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300" data-testid={`derived-badge-${project.id}`}>
                                  Σ derivado
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                Vista corregida: valores = fuente + diferencia suma-anual vs total-proyecto documentada. No se ha guardado, es solo de análisis.
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {project.data_quality_flags?.length > 0 && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" data-testid={`quality-flag-${project.id}`} />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                Incidencias de importación: {project.data_quality_flags.join(", ")}
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {project.manual_deviation_flag && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 flex-shrink-0" data-testid={`manual-deviation-badge-${project.id}`}>
                                  ⚠ editado
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                Editado manualmente y se aleja de la fuente verificada en: {project.manual_deviation_fields?.join(", ")}. Aviso enviado por email.
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">{project.project_type}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(project.status)}</TableCell>
                  <TableCell>
                    <span className="text-sm font-mono">
                      {project.year_start || "?"} - {project.year_end || "?"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">
                      {project.project_manager || <span className="italic opacity-40">—</span>}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-mono text-slate-500">{project.budget ? formatFullCurrency(project.budget) : "—"}</TableCell>
                  <TableCell className="text-right font-mono text-red-600">{formatFullCurrency(project.expenses)}</TableCell>
                  <TableCell className="text-right font-mono text-green-600">
                    <div className="flex items-center justify-end gap-1">
                      {formatFullCurrency(project.income)}
                      {mismatch && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <AlertTriangle className="w-3.5 h-3.5 text-amber-500" data-testid={`income-mismatch-${project.id}`} />
                          </TooltipTrigger>
                          <TooltipContent>
                            Ingresos no coincide con Subvención + Ahorro Fin. + Deducción Fiscal — revisar desglose
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {getBalanceIndicator(project.balance)}
                      <span className={`font-mono ${project.balance >= 0 ? "text-green-600" : "text-red-600"}`}>
                        {formatFullCurrency(project.balance)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono text-amber-600">{formatFullCurrency(project.subsidy)}</TableCell>
                  <TableCell className="text-right font-mono text-purple-600">{formatFullCurrency(project.financial_savings)}</TableCell>
                  <TableCell className="text-right font-mono text-sky-600">{formatFullCurrency(project.tax_deduction)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        size="icon" variant="ghost"
                        onClick={() => onEdit(project)}
                        data-testid={`edit-btn-${project.id}`} title="Editar proyecto"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon" variant="ghost" title="Ver desglose anual (proyecto-año)"
                        onClick={() => setExpandedId(expandedId === project.id ? null : project.id)}
                        data-testid={`toggle-annual-btn-${project.id}`}
                      >
                        {expandedId === project.id ? <ChevronUp className="w-4 h-4 text-primary" /> : <CalendarRange className="w-4 h-4 text-primary" />}
                      </Button>
                      <Button
                        size="icon" variant="ghost" title="Descargar ficha PDF"
                        onClick={() => onExportFicha(project, "pdf")}
                        disabled={fichaLoading === `${project.id}-pdf`}
                        data-testid={`ficha-pdf-btn-${project.id}`}
                      >
                        {fichaLoading === `${project.id}-pdf`
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <FileText className="w-4 h-4 text-red-500" />}
                      </Button>
                      <Button
                        size="icon" variant="ghost" title="Descargar ficha DOCX"
                        onClick={() => onExportFicha(project, "docx")}
                        disabled={fichaLoading === `${project.id}-docx`}
                        data-testid={`ficha-docx-btn-${project.id}`}
                      >
                        {fichaLoading === `${project.id}-docx`
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <FileDown className="w-4 h-4 text-blue-500" />}
                      </Button>
                      <Button
                        size="icon" variant="ghost" title="Ficha comercial (para clientes externos)"
                        onClick={() => onExportFicha(project, "comercial")}
                        disabled={fichaLoading === `${project.id}-comercial`}
                        data-testid={`ficha-comercial-btn-${project.id}`}
                      >
                        {fichaLoading === `${project.id}-comercial`
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <Leaf className="w-4 h-4 text-emerald-500" />}
                      </Button>
                      {(project.status === "Finalizado" || project.status?.includes("Finaliza")) && (
                        <Button
                          size="icon" variant="ghost" title="Ficha de cierre"
                          onClick={() => onOpenCierre(project)}
                          data-testid={`cierre-btn-${project.id}`}
                        >
                          <Award className="w-4 h-4 text-amber-500" />
                        </Button>
                      )}
                      <Button
                        size="icon" variant="ghost"
                        onClick={() => onDelete(project.id)}
                        data-testid={`delete-btn-${project.id}`} title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                {expandedId === project.id && (
                  <TableRow data-testid={`annual-expanded-row-${project.id}`}>
                    <TableCell colSpan={12} className="bg-muted/20 p-4">
                      <AnnualBreakdownTable token={token} projectName={project.project_name} projectTotal={project} compact />
                    </TableCell>
                  </TableRow>
                )}
                </Fragment>
                );
              })}
            </TableBody>
          </Table>
          </TooltipProvider>
        </div>
      </CardContent>
    </Card>
  );
}
